import React, { useState, useMemo } from 'react'
import { Drawer, Select, Typography, Empty } from 'antd'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import type { CalendarItem } from '@/types/contentPlanner'
import { useScheduleContent } from '@/hooks/useContentPlannerAPI'
import { toast } from 'sonner'

const { Text } = Typography

interface ScheduleDraftsDrawerProps {
  open: boolean
  dates: string[]         // sorted YYYY-MM-DD list
  allItems: CalendarItem[]
  onClose: () => void
  onDone: () => void      // clears selection + closes after save
}

export function ScheduleDraftsDrawer({
  open,
  dates,
  allItems,
  onClose,
  onDone,
}: ScheduleDraftsDrawerProps) {
  const { mutateAsync: scheduleContent } = useScheduleContent()

  // Saving guard: prevents drawer close + double-submit while mutations are in flight
  const [isSaving, setIsSaving] = useState(false)

  // Assignments: { 'YYYY-MM-DD': contentId }
  const [assignments, setAssignments] = useState<Record<string, string>>({})

  // Unscheduled drafts only
  const unscheduledDrafts = useMemo(
    () =>
      allItems.filter(
        (i) => !i.scheduled_date && ['draft', 'completed'].includes(i.status),
      ),
    [allItems],
  )

  // IDs already assigned in other rows — exclude from other dropdowns
  const assignedIds = new Set(Object.values(assignments))

  function getOptions(currentDate: string) {
    const currentId = assignments[currentDate]
    return unscheduledDrafts
      .filter((d) => d.id === currentId || !assignedIds.has(d.id))
      .map((d) => ({
        value: d.id,
        label: d.title || d.keyword || 'Untitled',
      }))
  }

  async function handleSave() {
    const entries = Object.entries(assignments)
    if (entries.length === 0) {
      toast.error('No assignments made')
      return
    }

    setIsSaving(true)
    try {
      // Run all schedule mutations and await every one before finishing.
      // The hook invalidates calendar keys on each success, so no extra
      // invalidation is needed here — we only gate the toast + handoff.
      const results = await Promise.allSettled(
        entries.map(([date, contentId]) =>
          scheduleContent({ contentId, scheduledDate: date }),
        ),
      )

      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - succeeded

      // Exactly one toast.
      if (failed === 0) {
        toast.success(`${succeeded} article${succeeded > 1 ? 's' : ''} scheduled`)
      } else {
        toast.error(
          `${failed} article${failed > 1 ? 's' : ''} failed to schedule` +
            (succeeded > 0 ? ` (${succeeded} succeeded)` : ''),
        )
      }

      // Close/refresh once everything has settled (covers the succeeded ones too).
      setAssignments({})
      onDone()
    } finally {
      setIsSaving(false)
    }
  }

  function handleClose() {
    // Don't allow the drawer to close mid-flight.
    if (isSaving) return
    setAssignments({})
    onClose()
  }

  return (
    <Drawer
      title="Assign Drafts to Dates"
      open={open}
      onClose={handleClose}
      width={600}
      maskClosable={!isSaving}
      keyboard={!isSaving}
      closable={!isSaving}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            className="bg-coral hover:bg-coral-hover text-white"
            onClick={handleSave}
            disabled={isSaving || Object.keys(assignments).length === 0}
          >
            {isSaving ? 'Saving…' : `Save ${Object.keys(assignments).length > 0 ? `(${Object.keys(assignments).length})` : ''} Assignments`}
          </Button>
        </div>
      }
    >
      {unscheduledDrafts.length === 0 ? (
        <Empty
          description="No unscheduled drafts available. Generate or create articles first."
          className="py-8"
        />
      ) : (
        <div className="divide-y divide-border">
          {dates.map((date) => {
            const options = getOptions(date)
            return (
              <div key={date} className="flex items-center gap-4 py-3">
                <div className="w-28 shrink-0">
                  <Text className="text-sm font-medium">{dayjs(date).format('ddd, MMM D')}</Text>
                </div>
                <Select
                  className="flex-1"
                  placeholder="Pick a draft…"
                  value={assignments[date] ?? null}
                  onChange={(val) =>
                    setAssignments((prev) => ({ ...prev, [date]: val }))
                  }
                  options={options}
                  allowClear
                  onClear={() =>
                    setAssignments((prev) => {
                      const next = { ...prev }
                      delete next[date]
                      return next
                    })
                  }
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>
            )
          })}
        </div>
      )}
    </Drawer>
  )
}
